import {
  CollectionReference,
  Firestore,
  Timestamp,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore/lite";
import { firebaseDb } from "../firebase/client-app";
import { DbBusinessConsumedSlot } from "../category-service/models";
import { DateTime } from "luxon";
import { DbBooking } from "./models";

export class ReservedBookingService {
  reservedRef(businessId: string, firestore: Firestore): CollectionReference {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_reserved_bookings",
    );
  }

  private consumedSlotsRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_consumed_slots",
    );
  }

  newReservedBookingId(businessId: string, firestore: Firestore): string {
    return doc(this.reservedRef(businessId, firestore)).id;
  }

  async getReservedBookings(
    businessId: string,
    date: DateTime,
    active = false,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBooking[]> {
    const startTime = date.startOf("day").toJSDate();
    const endTime = date.endOf("day").toJSDate();
    let bookingRef = query(
      this.reservedRef(businessId, firestore),
      where("booking_date", ">=", Timestamp.fromDate(startTime)),
      where("booking_date", "<=", Timestamp.fromDate(endTime)),
    );
    if (active) {
      bookingRef = query(
        bookingRef,
        where("expires_at", ">=", Timestamp.now()),
      );
    }
    const bookingDocs = await getDocs(bookingRef);
    return bookingDocs.docs.map((doc) => doc.data() as DbBooking);
  }

  async getConsumedSlots(
    businessId: string,
    startDate: DateTime,
    endDate: DateTime,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessConsumedSlot[]> {
    try {
      const consumedSlotsRef = query(
        this.consumedSlotsRef(firestore, businessId),
        where("start_date", ">=", Timestamp.fromDate(startDate.toJSDate())),
        where("end_date", "<=", Timestamp.fromDate(endDate.toJSDate())),
      );

      const consumedSlotDocs = await getDocs(consumedSlotsRef);

      return consumedSlotDocs.docs.map(
        (doc) => doc.data() as DbBusinessConsumedSlot,
      );
    } catch (error: any) {
      throw error;
    }
  }

  async updateConsumedSlotDuration(
    businessId: string,
    bookingId: string,
    duration: number,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    const q = query(
      this.consumedSlotsRef(firestore, businessId),
      where("booking_id", "==", bookingId),
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();

        if (data.start_date) {
          const startDate = data.start_date.toDate();
          const endDate = new Date(startDate);
          endDate.setMinutes(endDate.getMinutes() + duration);

          const updatedTimestamp = Timestamp.fromDate(endDate);

          await updateDoc(docSnapshot.ref, {
            end_date: updatedTimestamp,
          });
        }
      }
    }
  }
}

export const reservedBookingService = new ReservedBookingService();
